import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Checkbox, Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { getSortedList, useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import CloseIcon from 'mdi-react/CloseIcon'
import FilterOutlineIcon from 'mdi-react/FilterOutlineIcon'
import FormatListChecksIcon from 'mdi-react/FormatListChecksIcon'
import { useLocation, useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { CustomerSelect, getQueryFilterByCustomerId } from '@/components/CustomerSelect'
import { useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import adminResources from '@/modules/admin/resources'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { getEcomPolicyIdList } from '@/util/getEcomPolicyIdList'

import queries from '../queries'
import type { CostObject } from '../resources'

import AssignPoliciesPopup from './AssignPoliciesPopup'
import { CostObjectList } from './CostObjectList'
import PolicyFilter from './PolicyFilter'
import {
  ECOM_POLICY_FILTER_QUERY_KEY,
  TELE_POLICY_FILTER_QUERY_KEY,
  parseEcomPolicyFilterParam,
  parseTelePolicyFilterParam,
  serializeEcomPolicyFilterParam,
  serializeTelePolicyFilterParam,
} from './policyFilterSearchParams'
import type { PolicyId } from './types'

import './index.styles.scss'

const CostObjectListPage: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const translate = useTranslation()
  const { locale } = useAppContext()
  const isThreeStepIT = useIsThreeStepIT()

  const [isPolicyFilterPopupOpen, setIsPolicyFilterPopupOpen] = useState(false)
  const [isAssignPolicyPopupOpen, setIsAssignPolicyPopupOpen] = useState(false)
  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [{ querySort, querySortBy }] = useSortParams()
  const isAdmin = useIsAdmin()

  const [queryShowHidden, setQueryShowHidden] = useSearchParamsByKey('show-hidden')
  const [showHiddenFilter, setShowHiddenFilter] = useState(Boolean(queryShowHidden))
  const [queryCustomer] = useSearchParamsByKey('company')
  const currentSearchParams = new URLSearchParams(location.search)
  const selectedEcomPolicyFilterIds = parseEcomPolicyFilterParam(
    currentSearchParams.get(ECOM_POLICY_FILTER_QUERY_KEY)
  )
  const selectedTelePolicyFilterIds = parseTelePolicyFilterParam(
    currentSearchParams.get(TELE_POLICY_FILTER_QUERY_KEY)
  )

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  const { data: availableEcomPolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const { data: telePolicies } = useQuery(
    queries.adminTelePoliciesList(),
    adminResources.getTelePolicyList,
    {
      enabled: isAdmin,
    }
  )

  const ecomPolicies = availableEcomPolicies?.filter(policy => policy.isActive) || []

  useEffect(() => {
    setSearchFilter(querySearch || '')
    setShowHiddenFilter(queryShowHidden ? JSON.parse(queryShowHidden) : false)
  }, [querySearch, queryShowHidden])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const { data: costObjectList, isLoading: isLoadingCostObjects } = useQuery(
    queries.adminCostObjectList(),
    adminResources.getCostObjectList
  )

  const stringMatcher = (item: CostObject) => [
    item.name || '',
    translate(`cost_object_benefit_type/${item.benefitType}`),
    item.employeeNo || '',
    item.code,
    item.departmentName,
    item.departmentCode,
    item.customerName,
    item.email || '',
    item.accountingCode || '',
    item.dim1 || '',
    item.dim2 || '',
    item.dim3 || '',
    item.dim4 || '',
    item.workLocation || '',
  ]

  const costObjectsFiltered =
    costObjectList?.filter(costObject => {
      const isActive = !costObject.isHidden

      const isVisible = showHiddenFilter || (!showHiddenFilter && isActive)
      const ecomPolicyFilter =
        selectedEcomPolicyFilterIds && selectedEcomPolicyFilterIds?.length > 0
          ? selectedEcomPolicyFilterIds
          : undefined
      const isEcomPolicyFilterMet = ecomPolicyFilter
        ? !ecomPolicyFilter.find(
            policyId => !getEcomPolicyIdList(costObject.policyIds || '').includes(policyId)
          )
        : true

      const customerFilterMet = getQueryFilterByCustomerId(queryCustomer, costObject.customerId)

      const isTelePolicyFilterMet = getIsTelePolicyFilterMet({
        costObject,
        selectedTelePolicyFilterIds,
      })
      const isQueryFilterMet = getQueryFilter(searchFilter, stringMatcher)(costObject)

      return (
        isQueryFilterMet &&
        isVisible &&
        isEcomPolicyFilterMet &&
        isTelePolicyFilterMet &&
        customerFilterMet
      )
    }) || []

  const sortedList = getSortedList({ list: costObjectsFiltered, querySort, querySortBy, locale })
  const isRemovePolicyFilterButtonVisible =
    (selectedEcomPolicyFilterIds && selectedEcomPolicyFilterIds.length > 0) ||
    (selectedTelePolicyFilterIds && selectedTelePolicyFilterIds.length > 0)
  const isAssignPoliciesButtonVisible =
    isAdmin &&
    (availableEcomPolicies?.length !== 0 || telePolicies?.length !== 0) &&
    costObjectsFiltered?.length > 0
  const isFiltersPopupAvailable = ecomPolicies || telePolicies

  const setPolicyFilterQueryParams = ({
    ecomPolicyIds,
    telePolicyIds,
  }: {
    ecomPolicyIds: PolicyId[] | null
    telePolicyIds: PolicyId[] | null
  }) => {
    const nextSearchParams = new URLSearchParams(location.search)
    const serializedEcomPolicyIds = serializeEcomPolicyFilterParam(ecomPolicyIds)
    const serializedTelePolicyIds = serializeTelePolicyFilterParam(telePolicyIds)

    if (serializedEcomPolicyIds) {
      nextSearchParams.set(ECOM_POLICY_FILTER_QUERY_KEY, serializedEcomPolicyIds)
    } else {
      nextSearchParams.delete(ECOM_POLICY_FILTER_QUERY_KEY)
    }

    if (serializedTelePolicyIds) {
      nextSearchParams.set(TELE_POLICY_FILTER_QUERY_KEY, serializedTelePolicyIds)
    } else {
      nextSearchParams.delete(TELE_POLICY_FILTER_QUERY_KEY)
    }

    const nextSearch = nextSearchParams.toString()

    navigate(nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname)
  }

  return (
    <Container onSwipedLeft={() => navigate('/')} className="cost-objects">
      <Container.Header>
        <Translation id="Employees" />
      </Container.Header>

      <div className="table-actions">
        <Input
          id="cost-object-search"
          value={searchFilter}
          placeholder={translate('Search')}
          onChange={e => setSearchFilter(e.target.value)}
          className="mr-3"
        />

        <CustomerSelect
          filterData={costObjectList?.filter(item => item.customerId && item.customerName) || []}
          isLoading={isLoadingCostObjects}
        />

        <Checkbox
          id="show-hidden-input"
          className="show-hidden-input whitespace-nowrap"
          marginTop={0}
          marginLeft={0}
          checked={queryShowHidden ? JSON.parse(queryShowHidden) : false}
          onChange={() => {
            const updatedShowHidden = queryShowHidden
              ? JSON.parse(queryShowHidden)
                ? 'false'
                : 'true'
              : 'true'
            setQueryShowHidden(updatedShowHidden)
          }}
          label={<Translation id="Show hidden" />}
        />
        <div
          className="filters"
          style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
        >
          {isFiltersPopupAvailable && (
            <div
              className={`policy-filter ${
                isRemovePolicyFilterButtonVisible ? 'remove-policy-visible' : ''
              }`}
            >
              <Button
                className={twJoin(
                  isRemovePolicyFilterButtonVisible && 'mr-0 rounded-br-none rounded-tr-none',
                  !isRemovePolicyFilterButtonVisible && 'mr-3',
                  isThreeStepIT && isRemovePolicyFilterButtonVisible && 'border-r-0'
                )}
                Icon={<FilterOutlineIcon />}
                onClick={() => setIsPolicyFilterPopupOpen(true)}
              >
                <Translation id="Filter by policy" />
              </Button>

              {isRemovePolicyFilterButtonVisible && (
                <Button
                  className={twJoin(
                    'ml-0 mr-3 rounded-bl-none rounded-tl-none pl-1.5 pr-1',
                    isThreeStepIT ? 'border-l-0' : 'border-l-2 border-gray-300'
                  )}
                  Icon={<CloseIcon />}
                  onClick={() => {
                    setPolicyFilterQueryParams({
                      ecomPolicyIds: null,
                      telePolicyIds: null,
                    })
                  }}
                />
              )}

              {isPolicyFilterPopupOpen && (
                <PolicyFilter
                  currentEcomPolicyFilter={selectedEcomPolicyFilterIds}
                  currentTelePolicyFilter={selectedTelePolicyFilterIds}
                  onClose={() => setIsPolicyFilterPopupOpen(false)}
                  onApplyFilters={({ ecomPolicyIds, telePolicyIds }) => {
                    setPolicyFilterQueryParams({
                      ecomPolicyIds,
                      telePolicyIds,
                    })
                  }}
                />
              )}
            </div>
          )}

          {isAssignPolicyPopupOpen && (
            <AssignPoliciesPopup
              costObjects={sortedList}
              onClose={() => setIsAssignPolicyPopupOpen(false)}
            />
          )}

          {isAssignPoliciesButtonVisible && (
            <Button
              Icon={<FormatListChecksIcon />}
              onClick={() => setIsAssignPolicyPopupOpen(true)}
            >
              <Translation id="Assign policies" />
            </Button>
          )}
        </div>
      </div>

      <CostObjectList costObjects={sortedList} searchFilter={searchFilter} />
    </Container>
  )
}

export default CostObjectListPage

function getIsTelePolicyFilterMet({
  selectedTelePolicyFilterIds,
  costObject,
}: {
  selectedTelePolicyFilterIds: PolicyId[] | null
  costObject: CostObject
}) {
  const telePolicyFilter =
    selectedTelePolicyFilterIds && selectedTelePolicyFilterIds?.length > 0
      ? selectedTelePolicyFilterIds
      : undefined

  let isTelePolicyFilterMet = true
  if (!telePolicyFilter) return isTelePolicyFilterMet
  if (telePolicyFilter?.[0] === 'no-policy') {
    isTelePolicyFilterMet = !costObject.telePolicyId
    return isTelePolicyFilterMet
  }
  if (telePolicyFilter[0] !== costObject.telePolicyId) isTelePolicyFilterMet = false

  return isTelePolicyFilterMet
}
